import { toaster } from '@/components/ui/toaster';
import { CONTACT_SUMMARY_QUERY_PREFIX } from '@/hooks/queries/use-contact-summary-query';
import { planFeaturesQueryKey } from '@/hooks/queries/use-plan-features-query';
import api from '@/libs/axiosInstance';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';
import type {
  ApiErrorResponse,
  ApiResponse,
  CreatorProfileUnlockResponse,
  DiscoverySearchResponse,
  OrganizationFeaturesResponse,
  PlanScopedCreatorProfile,
} from '@fluenceur/types';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

/**
 * The creator to unlock: by dataset reference for a masked prospect (whose
 * handle the browser never holds), else by handle.
 */
export type ProfileUnlockTarget = { sourceCreatorId: string } | { username: string };

const unlockUrl = (target: ProfileUnlockTarget): string =>
  'sourceCreatorId' in target
    ? `/dashboard/creators/by-ref/${encodeURIComponent(target.sourceCreatorId)}/unlock`
    : `/dashboard/creators/${encodeURIComponent(target.username)}/unlock`;

/** The Discovery search pages, read without re-running the search. */
const DISCOVERY_SEARCH_PREFIX = ['creatorDiscovery', 'direct'] as const;

const isSameCreator = (
  shown: PlanScopedCreatorProfile,
  unlocked: PlanScopedCreatorProfile
): boolean =>
  shown.sourceCreatorId && unlocked.sourceCreatorId
    ? shown.sourceCreatorId === unlocked.sourceCreatorId
    : !!shown.username &&
      !!unlocked.username &&
      shown.username.toLowerCase() === unlocked.username.toLowerCase();

/**
 * The search pages with the unlocked creator put in place of its masked card.
 * The search itself is never fetched again: a new search would be counted.
 */
export function withUnlockedCreator(
  data: InfiniteData<DiscoverySearchResponse> | undefined,
  creator: PlanScopedCreatorProfile
): InfiniteData<DiscoverySearchResponse> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      creators: page.creators.map((shown) => (isSameCreator(shown, creator) ? creator : shown)),
    })),
  };
}

/**
 * Spends one of the free plan's monthly profile unlocks on a creator: the
 * handle, the email and the advanced stats appear everywhere the brand reads
 * them. A creator already unlocked costs nothing, and the server counts a
 * double click once. Past the allowance the API client opens the Essential
 * plan choice, so the refusal raises no toast here.
 */
export function useUnlockCreatorProfileAction() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    CreatorProfileUnlockResponse,
    AxiosError<ApiErrorResponse>,
    ProfileUnlockTarget
  >({
    mutationFn: async (target) => {
      const response = await api.post<ApiResponse<CreatorProfileUnlockResponse>>(unlockUrl(target));
      if (!response.data.data) throw new Error('Invalid response structure');
      return response.data.data;
    },
    onSuccess: ({ creator, quota }) => {
      queryClient.setQueryData<OrganizationFeaturesResponse>(planFeaturesQueryKey, (features) =>
        features ? { ...features, profileUnlocks: quota } : features
      );
      queryClient.setQueriesData<InfiniteData<DiscoverySearchResponse>>(
        { queryKey: DISCOVERY_SEARCH_PREFIX },
        (data) => withUnlockedCreator(data, creator)
      );
      for (const queryKey of [
        planFeaturesQueryKey,
        ['contact-resolve'],
        CONTACT_SUMMARY_QUERY_PREFIX,
        ['contact-profile'],
        ['contact-growth'],
        ['wishlist'],
        ['recommendations'],
        ['recommendations-history'],
        ['recommendations-history-batches'],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: (error) => {
      if (isPlanLimitError(error)) return;
      toaster.create({ title: t('planLocks.unlock.error'), type: 'error' });
    },
  });
}
